import { createAsyncThunk } from '@reduxjs/toolkit';
import { track as trackEvent } from '../../utils/analytics';
import { WEB_FEED_CACHE_KEY } from '../../utils/userScopedCaches';
import {
    setItems,
    setMixes,
    appendItems,
    setFreeLimit,
    setTrendingFreeLimit,
    setTasteNotice,
    setGoatCut,
    interleaveAiPicks,
    setLoading,
    setLoadingMore,
    setHasMore,
    setError,
    setLastFetchedAt,
    setLastFetchedTotals,
    setCursor,
    setFeedId,
    setTimeMode,
    setSurpriseActive,
    setDiscovery,
    setTrendingItems,
    setTrendingLoading,
    setTrendingFetchedAt,
    setTrendingError,
    setTrendingWindow,
} from '../slices/feedSlice';
import { connectDjpal } from './djpalThunk';
import { refreshTier } from './tierThunk';
import { warmImages } from '../../utils/imageWarm';
import { coverUrl } from '../../utils/coverUrl';
import { resolveMixCover } from '../../utils/nativeMixCover';
import { isMobileEnv } from '../../utils/webOnly';

const _isWeb = typeof window !== 'undefined' && window.electron && !window.electron.db;

// Start every landed page's covers downloading the moment the DATA lands
// (fetch, page-append, trending, snapshot restore). The cards lazy-load
// their <img>s, which alone made covers arrive just-in-time while
// scrolling — on slow connections the fetch lost the race and art visibly
// trailed the text. The px fork mirrors what .feed-card-artwork actually
// renders (120 desktop / 56 on the mobile breakpoint — same fork as
// FeedTab's FEED_ART_PX); the warm must match what the card renders
// (see utils/coverUrl.js).
const warmFeedCovers = (items = [], mixes = []) => {
    const px = isMobileEnv() ? 56 : 120;
    const urls = [
        ...(items || []).map((it) => {
            const t = it?.track || it;
            return coverUrl(t?.artwork || t?.artworkUrl || t?.image || null, px);
        }),
        ...(mixes || []).map((m) => coverUrl(resolveMixCover(m), px)),
    ];
    warmImages(urls, 80); // a full feed page + its mixes, not just a row
};

// Web-only session-stable cache. Persists the full feed state across
// reloads + tab-navigations so the user sees the SAME items within a
// 5-min stale window (the same window FeedTab.js already uses for its
// stale check). The persisted lastFetchedAt is what makes that
// suppression actually work — without it, every reload looks "stale"
// regardless of how recent the last fetch was.
//
// v2: stores items+mixes+cursor+feedId+lastFetchedAt (was v1: 30-item
// items+mixes only, used only for instant paint). Key bump invalidates
// any v1 entry on first read.
const WEB_FEED_CACHE_TTL = 24 * 60 * 60 * 1000;

function _currentUserKey() {
    try {
        const token = localStorage.getItem('djpal:token');
        if (!token) return null;
        const payload = JSON.parse(atob(token.split('.')[1]));
        return payload?.userId ? String(payload.userId) : null;
    } catch { return null; }
}

function _readWebFeedSnapshot() {
    if (!_isWeb) return null;
    try {
        const raw = localStorage.getItem(WEB_FEED_CACHE_KEY);
        if (!raw) return null;
        const snap = JSON.parse(raw);
        if (!snap || typeof snap !== 'object') return null;
        if (Date.now() - (snap.savedAt || 0) > WEB_FEED_CACHE_TTL) return null;
        if (snap.userId !== _currentUserKey()) return null;
        return snap;
    } catch { return null; }
}

function _writeWebFeedSnapshot({ items, mixes, cursor, feedId, lastFetchedAt, lastFetchedLikesTotal, lastFetchedFollowsTotal, timeMode, freeLimit }) {
    if (!_isWeb) return;
    try {
        const userId = _currentUserKey();
        if (!userId) return;
        const snap = {
            userId,
            savedAt: Date.now(),
            items: items || [],
            mixes: mixes || [],
            cursor: cursor || null,
            feedId: feedId || null,
            lastFetchedAt: lastFetchedAt || null,
            // Persisted so the page-reload mount-effect can compare
            // current Redux totals (which hydrate independently from
            // the likes slice) against the value at the last fetch.
            // Without this, the initial state value of 0 would always
            // look like "totals changed" on reload.
            lastFetchedLikesTotal: lastFetchedLikesTotal ?? null,
            lastFetchedFollowsTotal: lastFetchedFollowsTotal ?? null,
            // Persisted so the slider position matches the cached
            // items on reload. Without this, the slider resets to
            // default (1, fresh) while the items shown were built
            // for whatever the user last set — visually confusing.
            timeMode: typeof timeMode === 'number' ? timeMode : 1,
            // Free-plan cut that produced these items, so the wall is back
            // on reload without a refetch.
            freeLimit: freeLimit || null,
        };
        localStorage.setItem(WEB_FEED_CACHE_KEY, JSON.stringify(snap));
    } catch {
        // Quota exceeded, JSON failed, iOS private-browsing (0-byte
        // storage), etc. All non-fatal — the Redux state still drives
        // the live session; we just lose persistence.
    }
}

// (Logout wipes the snapshot too, via clearUserScopedCaches — the key lives
// in utils/userScopedCaches.js so authThunk doesn't have to import this file.)
function clearWebFeedSnapshot() {
    if (!_isWeb) return;
    try { localStorage.removeItem(WEB_FEED_CACHE_KEY); } catch { /* non-fatal */ }
}

// Dispatched on app bootstrap (web only). Restores items + mixes +
// cursor + feedId + lastFetchedAt so the existing 5-min stale check
// in FeedTab.js can actually skip the refetch when the user reloads
// (or navigates away and back to /) shortly after the last fetch.
export const hydrateWebFeedFromCache = () => (dispatch, getState) => {
    if (!_isWeb) return false;
    if (getState().feed.items.length > 0) return false;
    const snap = _readWebFeedSnapshot();
    if (!snap) return false;
    if (snap.items?.length) dispatch(setItems(snap.items));
    if (snap.mixes?.length) dispatch(setMixes(snap.mixes));
    warmFeedCovers(snap.items, snap.mixes);
    if (snap.cursor) dispatch(setCursor(snap.cursor));
    if (snap.feedId) dispatch(setFeedId(snap.feedId));
    dispatch(setFreeLimit(snap.freeLimit || null));
    if (snap.lastFetchedAt) dispatch(setLastFetchedAt(snap.lastFetchedAt));
    if (snap.lastFetchedLikesTotal != null || snap.lastFetchedFollowsTotal != null) {
        dispatch(setLastFetchedTotals({
            likes: snap.lastFetchedLikesTotal ?? null,
            follows: snap.lastFetchedFollowsTotal ?? null,
        }));
    }
    if (typeof snap.timeMode === 'number') {
        dispatch(setTimeMode(snap.timeMode));
    }
    return true;
};

async function getDjpalToken(dispatch) {
    let token = localStorage.getItem('djpal:token');
    if (!token) {
        await dispatch(connectDjpal()).unwrap();
        token = localStorage.getItem('djpal:token');
    }
    return token;
}

// NOTE: a previous version of this file also read the local SQLite tracks
// table (the device-wide cache of imported DJ libraries) and shipped them
// as a `localLibraryTracks` seed to the feed endpoint. That leaked across
// accounts: a second user signing in on the same machine got the first
// user's library seeded into THEIR recommendations.
//
// The server already has each user's library under user-library-service
// (`user_dj_*` tables, kept fresh by `src/backend/djLibrarySync.js` after
// every import). `music-info-server` pulls it per-userId via
// `getFeedData(userId)` and surfaces it as `djLibraryArtists`, so we don't
// need to (and shouldn't) send the device library up here.

// A result that never reached the server: the shims return `status: 0`
// for a thrown fetch (capacitor-shim) and the api-client / web shim surface
// the raw TypeError message. Anything with a real HTTP status is NOT this.
const TRANSPORT_ERROR_RE = /failed to fetch|load failed|network ?error|networkerror|the internet connection appears to be offline|request timed out|service unavailable|econnreset|econnrefused|enotfound|etimedout|socket hang up/i;
export function isTransportError(result) {
    if (!result || typeof result !== 'object' || !result.error) return false;
    if (result.status === 0) return true;
    if (typeof result.status === 'number' && result.status > 0) return false;
    return typeof result.error === 'string' && TRANSPORT_ERROR_RE.test(result.error);
}

// The feed server cut the list to the free plan but this app knows the user is
// GOAT: the server could not confirm the plan (its subscription check is down
// or unconfigured — it fails closed to free). Its "Unlock with GOAT" wall
// would be a dead button for a paying user (useTierGate opens nothing for
// GOAT), so keep what came back without the wall and say why it is short:
// feed.goatCut[list] drives a banner with Try again (FeedTab) until a later
// load of that list comes back uncut.
const goatCutByServer = (getState, freeLimit) =>
    Boolean(freeLimit) && getState().tier?.effectiveTier === 'goat';

export const fetchFeed = createAsyncThunk(
    'feed/fetch',
    async (options = {}, { dispatch, getState }) => {
        const { force = false, surprise = false } = options || {};
        const fetchStartedAt = Date.now();
        if (surprise) trackEvent('feed_rerolled');
        try {
            dispatch(setLoading(true));
            dispatch(setError(null));
            // Drive the button's active state: a Surprise roll lights it up;
            // any normal/personalized fetch (refresh, slider, reload) clears it.
            dispatch(setSurpriseActive(surprise));

            // On force-refresh (or a Surprise Me roll), drop the persisted
            // snapshot up front. If the network fetch fails after this point,
            // the next page reload won't restore a stale snapshot and
            // incorrectly suppress the refetch via the lastFetchedAt stale check.
            if (force || surprise) clearWebFeedSnapshot();

            const timeMode = getState().feed.timeMode ?? 1;

            const token = await getDjpalToken(dispatch);
            if (!token) {
                dispatch(setError('Could not connect. Please try again.'));
                dispatch(setLoading(false));
                return;
            }

            // Gather recently-liked tracks from Redux (covers server-side caching lag)
            const localLikes = getState().likes?.likedTracks || [];
            const recentLikes = localLikes.slice(0, 50).map(t => ({
                artist: t.artist,
                title: t.title,
                releaseDate: t.release_date || t.releaseDate || null,
            }));

            const result = await window.electron.djpal.fetchFeed(token, {
                timeMode,
                pageSize: 150,
                platform: _isWeb ? 'web' : 'electron',
                recentLikes,
                // Bypasses the server pool cache. Set on manual refresh
                // and on time-mode slider change (the cached pool was
                // built with the previous slider position).
                force,
                // Surprise Me: server ignores likes/follows and returns a
                // random feed within the current slider window (not cached).
                surprise,
            });

            if (result?.error) {
                // Tier not resolved yet — the backend gate couldn't read the
                // user's subscription because auth is still settling on cold
                // start (renderer hydrates `isAuthenticated` from cache and
                // prefetches the feed before the main-process tier cache is
                // populated). This is NOT a paywall; retry a few times with
                // backoff and keep the spinner up instead of flashing a false
                // "upgrade"/error.
                if (result.retryable || result.error === 'TIER_NOT_READY') {
                    const attempt = (options.__tierRetry || 0) + 1;
                    if (attempt <= 4) {
                        setTimeout(() => {
                            dispatch(fetchFeed({ ...options, __tierRetry: attempt }));
                        }, 300 * attempt);
                        return; // leave loading=true; the retry resolves it
                    }
                }
                if (result.upgradeRequired || result.error === 'UPGRADE_REQUIRED') {
                    // The backend feature gate denied the feed. But the
                    // renderer's tier (state.tier, resolved from
                    // /api/tier/reconciled — Stripe ∪ IAP ∪ trial) and the
                    // backend gate (tierCache → /me subscription_type) are
                    // computed independently and can briefly DISAGREE: a stale
                    // tierCache, an expired access token, or an entitlement
                    // source one path honors and the other doesn't. When the UI
                    // thinks GOAT but the gate says free, For You falls into a
                    // dead zone — `canAccess` is true (so the "Unlock with
                    // GOAT" screen never shows) yet the fetch is denied — and
                    // the user gets a meaningless generic "Nothing here yet".
                    //
                    // Before trusting the denial, re-resolve tier. refreshTier()
                    // re-pulls /me (which ALSO refreshes the backend tierCache
                    // via auth:me → setAuthContext) and reconciled, so both
                    // sources end up fresh and agreeing.
                    if (!options.__tierResync) {
                        try { await dispatch(refreshTier()); } catch { /* keep going */ }
                        if (getState().tier?.effectiveTier === 'goat') {
                            // It was a desync; the gate is now fresh — retry once.
                            dispatch(fetchFeed({ ...options, __tierResync: true }));
                            return; // leave loading=true; the retry resolves it
                        }
                        // Genuinely not entitled. Don't show the generic error
                        // box — FeedTab renders its own "Unlock with GOAT"
                        // screen off the (now-free) renderer tier. Just stop the
                        // spinner and let that take over.
                        dispatch(setLoading(false));
                        return;
                    }
                    // Already re-synced and still denied — surface a clean message.
                    dispatch(setError('For You is a GOAT feature.'));
                    dispatch(setLoading(false));
                    return;
                }
                // Transport-level failure (no HTTP status / fetch threw): the
                // radio isn't up yet. Seen live on Android 2026-08-19: the
                // resume-recovery fetch fired ~350ms after the process was
                // unfrozen, before connectivity was restored → "Failed to
                // fetch", and the error state rendered "Nothing here yet /
                // sync your library" to a user with hundreds of likes. Requests
                // 200ms later succeeded. Same race on cold start and on
                // Wi-Fi↔cellular handover. Retry with backoff before giving up;
                // the spinner stays up meanwhile. Capped so a genuinely offline
                // user still reaches the (now honest) error state in ~7s.
                if (isTransportError(result)) {
                    const attempt = (options.__netRetry || 0) + 1;
                    if (attempt <= 3) {
                        console.warn(`[Feed v3] transport error (${result.error}) — retry ${attempt}/3`);
                        setTimeout(() => {
                            dispatch(fetchFeed({ ...options, __netRetry: attempt }));
                        }, 500 * attempt * attempt); // 0.5s, 2s, 4.5s
                        return; // leave loading=true; the retry resolves it
                    }
                }
                console.error('[Feed v3] error:', result.error);
                trackEvent('feed_load_failed', {
                    mode: surprise ? 'surprise' : 'personal',
                    reason: isTransportError(result) ? 'transport' : 'server',
                    retries: options.__netRetry || 0,
                    duration_ms: Date.now() - fetchStartedAt,
                });
                dispatch(setError('Failed to load feed. Please try again.'));
                dispatch(setLoading(false));
                return;
            }

            const data = result?.data || result;
            const items = data.items || [];
            const cursor = data.cursor || null;
            const hasMore = data.hasMore ?? false;
            const feedId = data.feedId || null;

            const fetchedAt = Date.now();
            // `null` (not 0) when the slice hasn't loaded yet. On desktop the
            // bootstrap fetch (App.js) races ahead of fetchLikes /
            // fetchFollowedArtists, so the totals here would read 0; a second
            // later the real counts land, FeedTab saw "totals changed", cleared
            // the freshly painted feed to skeletons and refetched — the
            // "old list flashes then refreshes" effect. Unknown must never
            // compare as changed.
            const likesState = getState().likes;
            const followsState = getState().followedArtists;
            const likesTotalAtFetch = likesState?.loaded ? (likesState.total || 0) : null;
            const followsTotalAtFetch = followsState?.loaded ? (followsState.total || 0) : null;

            if (items.length === 0) {
                // Empty response. Could be:
                //   (a) Brand-new user with no likes/follows/library — the
                //       intended case for auto-switch + the trending banner.
                //   (b) Transient server hiccup (pool race, cache miss,
                //       expired feedId) on a returning user mid-session.
                //
                // We deliberately do NOT clear items / mixes / cursor here.
                // For case (a) Redux items is already [] (initial state) so
                // not clearing is a no-op; for case (b) clearing would blow
                // away a perfectly good populated feed, then the
                // items.length === 0 dep on the refetch effect would loop
                // bouncing on the empty server response — exactly the
                // "feed reloads and gets stuck on the skeleton mid-session"
                // bug. The fetch metadata still gets recorded so the
                // banner / auto-switch effects can react when this really
                // is the brand-new-user case.
                dispatch(setLastFetchedAt(fetchedAt));
                dispatch(setLastFetchedTotals({
                    likes: likesTotalAtFetch,
                    follows: followsTotalAtFetch,
                }));
                dispatch(setLoading(false));
                trackEvent('feed_loaded', {
                    mode: surprise ? 'surprise' : 'personal',
                    time_mode: timeMode,
                    item_count: 0,
                    duration_ms: Date.now() - fetchStartedAt,
                });
                return;
            }

            dispatch(setItems(items));
            dispatch(setMixes(data.mixes || []));
            warmFeedCovers(items, data.mixes || []);
            dispatch(setDiscovery(typeof data.discovery === 'number' ? data.discovery : 0));
            dispatch(setError(null)); // clear any watchdog/transient error a late success lands on
            dispatch(setCursor(cursor));
            dispatch(setFeedId(feedId));
            // Free plan: main / music-info-server cut the page to the first N
            // items and reported the rest as a count — there are no more pages.
            let freeLimit = data.freeLimit || null;
            if (goatCutByServer(getState, freeLimit)) {
                // A stale plan in main's tierCache cuts the same way —
                // re-resolve the tier and ask once more before giving up.
                if (!options.__goatCutResync) {
                    try { await dispatch(refreshTier()); } catch { /* keep going */ }
                    dispatch(fetchFeed({ ...options, __goatCutResync: true }));
                    return; // leave loading=true; the retry resolves it
                }
                console.warn('[feed] server cut the feed to the free plan for a GOAT user');
                freeLimit = null;
                dispatch(setGoatCut({ list: 'feed', value: true }));
                trackEvent('feed_goat_cut_shown', { list: 'feed' });
            } else {
                dispatch(setGoatCut({ list: 'feed', value: false }));
            }
            dispatch(setFreeLimit(freeLimit));
            dispatch(setHasMore(freeLimit ? false : hasMore));
            // A refresh / Surprise Me that came back with today's remembered
            // taste: say so instead of silently showing the same five.
            const tasteCached = (force || surprise) && freeLimit?.taste === 'cached';
            if (freeLimit || !data.freeLimit) {
                dispatch(setTasteNotice(tasteCached
                    ? `These are today's ${freeLimit.shown} free picks — GOAT refreshes on demand and unlocks the other ${(freeLimit.moreCount || 0).toLocaleString()}.`
                    : null));
            }
            if (tasteCached) {
                trackEvent('free_limit_hit', { feature: 'for_you', kind: 'refresh', list: 'tracks', mode: surprise ? 'surprise' : 'personal', more_count: freeLimit.moreCount });
            } else if (freeLimit?.moreCount > 0) {
                trackEvent('free_limit_hit', { feature: 'for_you', kind: 'items', list: 'tracks', more_count: freeLimit.moreCount });
            }
            dispatch(setLastFetchedAt(fetchedAt));
            dispatch(setLastFetchedTotals({
                likes: likesTotalAtFetch,
                follows: followsTotalAtFetch,
            }));
            dispatch(setLoading(false));

            _writeWebFeedSnapshot({
                items,
                mixes: data.mixes || [],
                cursor,
                feedId,
                lastFetchedAt: fetchedAt,
                lastFetchedLikesTotal: likesTotalAtFetch,
                lastFetchedFollowsTotal: followsTotalAtFetch,
                timeMode,
                freeLimit,
            });

            trackEvent('feed_loaded', {
                mode: surprise ? 'surprise' : 'personal',
                time_mode: timeMode,
                item_count: items.length,
                duration_ms: Date.now() - fetchStartedAt,
            });

            // Fire-and-forget: hydrate "Picked for you" after the main feed
            // has rendered. Decoupled from the main fetch so the initial
            // paint doesn't wait on NODE2VEC.
            // "Picked for you" rows interleave into the list — past the free
            // cut they would leak extra items, so the free plan skips them
            // (the server serves none either).
            if (feedId && !freeLimit) dispatch(fetchFeedAiPicks(feedId));
        } catch (error) {
            console.error('Feed error:', error);
            dispatch(setError('Failed to load feed. Please try again.'));
            dispatch(setLoading(false));
        }
    }
);

export const fetchFeedAiPicks = createAsyncThunk(
    'feed/fetchAiPicks',
    async (feedId, { dispatch, getState }) => {
        try {
            if (!feedId) return;
            const token = await getDjpalToken(dispatch);
            if (!token) return;

            const result = await window.electron.djpal.fetchFeedAiPicks(token, feedId);
            if (result?.error) {
                console.warn('[Feed v3] aipicks error:', result.error);
                return;
            }
            const data = result?.data || result;
            const items = data?.items || [];
            if (!items.length) return;

            // Only interleave if the user hasn't started a new feed (feedId
            // mismatch would mean a stale response landing on a newer feed).
            if (getState().feed.feedId !== feedId) return;
            dispatch(interleaveAiPicks(items));
            warmFeedCovers(items);
        } catch (error) {
            console.warn('[Feed v3] aipicks failed (non-fatal):', error);
        }
    }
);

// Trending tracks — global chart, no personalization. Server caches for 30 min.
// Window defaults to 90 days; pass windowDays / limit to override.
export const fetchTrendingFeed = createAsyncThunk(
    'feed/fetchTrending',
    async (opts = {}, { dispatch, getState }) => {
        const { windowDays = 90, limit = 100, force = false } = opts || {};
        try {
            dispatch(setTrendingLoading(true));
            dispatch(setTrendingError(null));

            const token = await getDjpalToken(dispatch);
            if (!token) {
                dispatch(setTrendingError('Could not connect.'));
                dispatch(setTrendingLoading(false));
                return;
            }

            const result = await window.electron.djpal.fetchTrending(token, {
                windowDays,
                limit,
                platform: _isWeb ? 'web' : 'electron',
                // Server invalidates the cached trending entry on force,
                // so a stale empty result doesn't survive the refresh.
                force,
            });
            if (result?.error) {
                // Same radio-not-up-yet race as fetchFeed (see isTransportError).
                if (isTransportError(result)) {
                    const attempt = (opts.__netRetry || 0) + 1;
                    if (attempt <= 3) {
                        console.warn(`[Trending] transport error (${result.error}) — retry ${attempt}/3`);
                        setTimeout(() => {
                            dispatch(fetchTrendingFeed({ ...opts, __netRetry: attempt }));
                        }, 500 * attempt * attempt);
                        return; // leave loading=true; the retry resolves it
                    }
                }
                console.error('[Trending] error:', result.error);
                trackEvent('feed_load_failed', {
                    mode: 'trending',
                    reason: isTransportError(result) ? 'transport' : 'server',
                    retries: opts.__netRetry || 0,
                });
                dispatch(setTrendingError('Failed to load trending. Please try again.'));
                dispatch(setTrendingLoading(false));
                return;
            }

            const data = result?.data || result;
            const items = data.items || [];

            // Filter out tracks the user can't actually play (no YouTube ID, no
            // SoundCloud URL, no preview/sample, no local file). Matches the For
            // You feed which never shows un-playable items.
            const isPlayable = (it) => {
                const t = it?.track;
                if (!t) return false;
                if (t.youtubeId && t.youtubeId !== '-1') return true;
                if (t.soundcloudUrl) return true;
                if (t.previewUrl || t.sampleUrl || t.preview_url || t.preview) return true;
                if (t.filePath) return true;
                return false;
            };
            const playable = items.filter(isPlayable);

            // Weighted shuffle by playedCount: tracks with more plays appear
            // earlier more often, but not deterministically. With low counts
            // (typical: 1-5 plays) linear weight gives meaningful preference —
            // a 3-play track is 3× more likely than a 1-play track at any
            // given position. Server caches the trending set for 30 min, so
            // without weighting every refresh would show an identical lineup.
            const result_items = [];
            const pool = playable.map((it) => ({
                item: it,
                weight: Math.max(1, it.playedCount || it.mixRefcount || 1),
            }));
            while (pool.length > 0) {
                const total = pool.reduce((s, e) => s + e.weight, 0);
                let r = Math.random() * total;
                let pickedIdx = 0;
                for (let i = 0; i < pool.length; i++) {
                    r -= pool[i].weight;
                    if (r <= 0) { pickedIdx = i; break; }
                }
                result_items.push(pool[pickedIdx].item);
                pool.splice(pickedIdx, 1);
            }
            dispatch(setTrendingItems(result_items));
            let trendingFreeLimit = data.freeLimit || null;
            if (goatCutByServer(getState, trendingFreeLimit)) {
                console.warn('[feed] server cut trending to the free plan for a GOAT user');
                trendingFreeLimit = null;
                dispatch(setGoatCut({ list: 'trending', value: true }));
                trackEvent('feed_goat_cut_shown', { list: 'trending' });
            } else {
                dispatch(setGoatCut({ list: 'trending', value: false }));
            }
            dispatch(setTrendingFreeLimit(trendingFreeLimit));
            const trendingTasteCached = force && trendingFreeLimit?.taste === 'cached';
            // only ever SET from here: blanking it blanked the For You notice that had just appeared
            if (trendingTasteCached) dispatch(setTasteNotice(`These are today's top ${trendingFreeLimit.shown} free picks — GOAT refreshes trending on demand.`));
            if (trendingTasteCached) {
                trackEvent('free_limit_hit', { feature: 'for_you', kind: 'refresh', list: 'trending', more_count: trendingFreeLimit.moreCount });
            } else if (trendingFreeLimit?.moreCount > 0) {
                trackEvent('free_limit_hit', { feature: 'for_you', kind: 'items', list: 'trending', more_count: trendingFreeLimit.moreCount });
            }
            warmFeedCovers(result_items);
            dispatch(setTrendingWindow({ from: data.windowFrom || null, to: data.windowTo || null }));
            dispatch(setTrendingFetchedAt(Date.now()));
            dispatch(setTrendingLoading(false));
            trackEvent('feed_loaded', { mode: 'trending', item_count: result_items.length, window_days: windowDays });
        } catch (error) {
            console.error('Trending error:', error);
            dispatch(setTrendingError('Failed to load trending. Please try again.'));
            dispatch(setTrendingLoading(false));
        }
    }
);

export const fetchMoreFeed = createAsyncThunk(
    'feed/fetchMore',
    async (_, { dispatch, getState }) => {
        try {
            const { feed } = getState();
            if (feed.isLoadingMore || feed.isLoading || !feed.hasMore || feed.freeLimit) return;

            const cursor = feed.cursor;
            if (!cursor) {
                dispatch(setHasMore(false));
                return;
            }

            dispatch(setLoadingMore(true));

            const token = await getDjpalToken(dispatch);
            if (!token) {
                dispatch(setLoadingMore(false));
                return;
            }

            const result = await window.electron.djpal.fetchFeed(token, { cursor, pageSize: 50 });

            if (result?.error) {
                console.warn('[Feed v3] fetchMore error:', result.error);
                dispatch(setLoadingMore(false));
                return;
            }

            const data = result?.data || result;
            const items = data.items || [];
            const newCursor = data.cursor || null;
            const hasMore = data.hasMore ?? false;

            if (items.length > 0) {
                dispatch(appendItems(items));
                warmFeedCovers(items);
            }
            dispatch(setCursor(newCursor));
            dispatch(setHasMore(hasMore));
            dispatch(setLoadingMore(false));
            trackEvent('feed_loaded', { mode: 'personal', page: 'more', item_count: items.length });
        } catch (error) {
            console.error('Feed loadMore error:', error);
            dispatch(setLoadingMore(false));
        }
    }
);
